import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { LogoMark } from './LogoMark';
import { useAuth } from '@/lib/auth';
import { useAdminApp } from '@/lib/apps';
import { listTitle, statusOf, useAdminPost } from '@/lib/posts';
import { useAppStoreStatus } from '@/lib/app-store-connect';
import {
  EditorBarProvider,
  PageActionsProvider,
  useEditorBar,
  usePageActions,
} from '@/lib/editor-bar';
import { useTheme } from '@/lib/theme';
import { useWaitingTickets } from '@/lib/tickets';

/**
 * The icon rail, its paths taken verbatim from the design.
 *
 * 66px wide, and every item is an icon with its **full name** under it. It was
 * 54px with three-letter mono codes — "OVR", "APP", "BLG" — which is a legend
 * somebody has to learn before the rail helps them. Twelve more pixels buys the
 * word itself.
 */
const NAV = [
  { to: '/dashboard', label: 'Overview', d: 'M2 10.5h3l2-5.5 2.5 8.5 1.8-3H14' },
  {
    to: '/apps',
    label: 'Apps',
    d: 'M2.6 2.6h4.2v4.2H2.6zM9.2 2.6h4.2v4.2H9.2zM2.6 9.2h4.2v4.2H2.6zM9.2 9.2h4.2v4.2H9.2z',
  },
  { to: '/blog', label: 'Blog', d: 'M3 3.4h10M3 6.6h10M3 9.8h7M3 13h4' },
  {
    to: '/tickets',
    label: 'Tickets',
    // An envelope: the reports arrive as mail and are answered as mail.
    d: 'M2.4 4.2h11.2v7.6H2.4zM2.4 4.6l5.6 4 5.6-4',
  },
  {
    to: '/settings',
    label: 'Settings',
    d: 'M8 5.7a2.3 2.3 0 100 4.6 2.3 2.3 0 000-4.6M8 1.8v1.7M8 12.5v1.7M2.2 8h1.7M12.1 8h1.7M3.9 3.9l1.2 1.2M10.9 10.9l1.2 1.2M12.1 3.9l-1.2 1.2M5.1 10.9l-1.2 1.2',
  },
] as const;

const SUN =
  'M8 2.4v1.5M8 12.1v1.5M2.4 8h1.5M12.1 8h1.5M4.1 4.1l1.1 1.1M10.8 10.8l1.1 1.1M11.9 4.1l-1.1 1.1M5.2 10.8l-1.1 1.1M8 5.4a2.6 2.6 0 100 5.2 2.6 2.6 0 000-5.2';
const MOON = 'M13 9.9A5.6 5.6 0 016.1 3a5.8 5.8 0 100 11.6c3 0 5.6-2 6.9-4.7z';

const Icon = ({ d, size = 16 }: { d: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    style={{ flex: 'none' }}
  >
    <path
      d={d}
      stroke="currentColor"
      strokeWidth="1.35"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * What the header says this screen is, and where it sits.
 *
 * The title is the screen; the crumb underneath is the path to it, in the
 * design's own lower-case mono. Both come from the same queries the pages
 * render — react-query dedupes the observers, so naming the app and the post
 * costs no extra request, and a raw uuid tells a person nothing about where
 * they are, which is the one job a breadcrumb has.
 *
 * Called unconditionally: the shell is a layout route, so moving from /apps to
 * /apps/:id re-renders rather than remounts it, and a hook inside a branch
 * would change the hook count between renders. The queries are disabled when
 * there is no id.
 */
function useHeading(): { title: string; crumb: string } {
  const { pathname } = useLocation();

  const editingApp = /^\/apps\/[^/]+$/.test(pathname);
  const app = useAdminApp(editingApp ? (pathname.split('/')[2] ?? '') : '').data;
  const editingPost = /^\/blog\/[^/]+$/.test(pathname);
  const post = useAdminPost(editingPost ? (pathname.split('/')[2] ?? '') : '').data;
  // Unconditional, like the two above: a hook behind an `if` is a hook that
  // changes count between renders, and React counts them by position.
  const appStore = useAppStoreStatus();

  if (editingApp) {
    return { title: app?.name ?? '…', crumb: `content / apps / ${app?.slug ?? '…'}` };
  }
  if (pathname.startsWith('/apps')) return { title: 'Apps', crumb: 'content / apps' };
  if (editingPost) {
    return {
      title: post ? listTitle(post.title).text : '…',
      crumb: `content / blog / ${post?.slug ?? '…'}`,
    };
  }
  if (pathname.startsWith('/blog')) return { title: 'Blog', crumb: 'content / blog' };
  if (pathname.startsWith('/tickets')) {
    return { title: 'Tickets', crumb: 'from the website' };
  }
  if (pathname.startsWith('/settings')) {
    return { title: 'Settings', crumb: 'studio configuration' };
  }
  /*
   * The design's own copy says `live`. This said `not connected` — hard-coded,
   * because every figure on the screen was the design's sample and a header
   * calling it live was the screen's most confident lie.
   *
   * It is no longer written down. The server holding the credentials is asked,
   * so the word changes when the configuration does rather than when somebody
   * remembers to edit this line — which is the same failure one word further
   * along.
   *
   * `configured`, not the design's `live`, and the distinction is the point:
   * `connected` here means the four credentials are readable and the key is one
   * ES256 can use. It does **not** mean Apple has ever answered. `live` is the
   * word for a screen with figures on it, and belongs to the commit that
   * fetches one.
   */
  /*
   * Three answers, and the third is a real one. `isPending` covers the gap
   * between render and reply; it does **not** cover a reply that never came —
   * a failed query is not pending, so falling through to `data?.connected`
   * would print `not connected` as a fact nobody was told, permanently,
   * whenever the session has expired or the API is unreachable.
   */
  if (appStore.isPending) return { title: 'Overview', crumb: 'app store connect · …' };
  /*
   * `unknown` only when there is genuinely no answer to give.
   *
   * A refetch that fails after a success leaves `status: 'error'` with the
   * previous `data` still in hand — so checking `isError` first put `unknown`
   * in the header while the Overview below it said `configured` and explained
   * that it was showing the last answer. Two different answers to one query, at
   * the same time, on the same screen.
   */
  if (appStore.isError && !appStore.data) {
    return { title: 'Overview', crumb: 'app store connect · unknown' };
  }
  return {
    title: 'Overview',
    crumb: `app store connect · ${appStore.data?.connected ? 'configured' : 'not connected'}`,
  };
}

/**
 * The save bar: an unsaved marker and the button.
 *
 * "Unsaved changes" used to be printed unconditionally, which made it a label
 * rather than a signal — it said the same thing on a page nobody had touched
 * as on one with real edits waiting. It appears only when there is something
 * to save, and the design gives it a pulsing dot so it reads as a state rather
 * than as a caption.
 */
function SaveBar() {
  const { bar } = useEditorBar();
  if (!bar) return null;

  return (
    <>
      {bar.dirty && !bar.error ? (
        <span
          className="hidden items-center gap-1.5 font-mono text-[10.5px] sm:inline-flex"
          style={{ color: 'var(--warn)' }}
        >
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: 'var(--warn)', animation: 'sonasi-pulse 1.8s infinite' }}
          />
          Unsaved
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => bar.save()}
        // Disabled with nothing to save, so pressing it cannot write a record
        // identical to the one already stored and report it as a change.
        disabled={!bar.dirty || bar.saving}
        className="btn-accent focus-ring min-h-8 cursor-pointer px-3 py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
      >
        {bar.saving ? 'Saving…' : 'Save'}
      </button>
    </>
  );
}

/**
 * What just happened, said once and then gone.
 *
 * Derived from the save bar rather than pushed by the pages: nothing had to be
 * added to the editor contract, and no page can forget to report an outcome it
 * did have.
 *
 * Two triggers, not one. A save finishing — `saving` going from true to false
 * with nothing wrong — is the success. A failure is **the error appearing**,
 * not the same transition: an editor that reports a refusal without ever
 * setting `saving` is a real shape, and keying the failure off the transition
 * alone means that refusal is never shown to anyone.
 */
function useSaveToast(): { message: string; ok: boolean } | null {
  const { bar } = useEditorBar();
  const [toast, setToast] = useState<{ message: string; ok: boolean } | null>(null);
  const wasSaving = useRef(false);
  const lastError = useRef<string | null>(null);

  const saving = bar?.saving ?? false;
  const error = bar?.error ?? null;

  useEffect(() => {
    if (error && error !== lastError.current) {
      lastError.current = error;
      wasSaving.current = false;
      setToast({ message: error, ok: false });
      return;
    }
    if (!error) lastError.current = null;

    if (saving) {
      wasSaving.current = true;
      return;
    }
    if (!wasSaving.current) return;
    wasSaving.current = false;
    setToast({ message: 'Saved', ok: true });
  }, [saving, error]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.ok ? 2400 : 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  return toast;
}

function Toast() {
  const toast = useSaveToast();
  if (!toast) return null;

  return (
    <div
      className="pointer-events-none fixed top-3.5 right-0 left-0 z-[60] flex justify-center"
      // Announced, not just shown: a save that failed while somebody was
      // looking at the field they changed is the case this exists for.
      role="status"
      aria-live="polite"
    >
      <span
        className="inline-flex max-w-[90vw] items-center gap-2.5 rounded-full py-2 pr-4 pl-3"
        style={{
          background: 'var(--panel)',
          border: `1px solid ${toast.ok ? 'var(--line2)' : 'var(--danger)'}`,
          boxShadow: '0 16px 40px rgba(0, 0, 0, 0.55)',
          animation: 'sonasi-toast-in 0.18s ease-out',
        }}
      >
        <span
          aria-hidden="true"
          className="flex h-4 w-4 flex-none items-center justify-center rounded-full text-[10px] font-bold"
          style={{
            background: toast.ok ? 'var(--ok)' : 'var(--danger)',
            color: 'var(--on-accent)',
          }}
        >
          {toast.ok ? '✓' : '!'}
        </span>
        <span className="text-[12.5px]" style={{ color: 'var(--text)' }}>
          {toast.message}
        </span>
      </span>
    </div>
  );
}

export function AdminShell() {
  return (
    <EditorBarProvider>
      <PageActionsProvider>
        <Shell />
      </PageActionsProvider>
    </EditorBarProvider>
  );
}

function Shell() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { title, crumb } = useHeading();
  const { bar } = useEditorBar();
  const { actions, segments } = usePageActions();
  const { theme, toggle } = useTheme();
  /*
   * The badge's number. Asked for here rather than in the page, because the
   * rail is on every screen and the point of the badge is that somebody
   * reading the blog finds out a report came in.
   */
  const waiting = useWaitingTickets();

  const name = user?.name?.trim() || user?.email || '';
  const initial = (name[0] ?? '·').toUpperCase();
  const themeLabel = theme === 'light' ? 'Switch to dark' : 'Switch to light';

  return (
    <div className="grid min-h-screen grid-cols-[66px_minmax(0,1fr)]">
      <aside
        className="sticky top-0 box-border flex h-screen flex-col items-center gap-3.5 border-r py-2.5"
        style={{ borderColor: 'var(--line)', background: 'var(--panel)' }}
      >
        <LogoMark size={30} />

        <nav className="flex w-full flex-col items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              title={item.label}
              /*
               * The count belongs in the name, not only in the badge: a
               * coloured circle says nothing to a screen reader, and the
               * number is the whole reason the circle is there.
               */
              aria-label={
                item.to === '/tickets' && waiting > 0
                  ? `${item.label}, ${waiting} waiting`
                  : item.label
              }
              aria-current={
                pathname.startsWith(item.to) || (item.to === '/dashboard' && pathname === '/')
                  ? 'page'
                  : undefined
              }
              className="rail-item focus-ring relative flex w-[54px] flex-col items-center justify-center gap-[3px] rounded-[10px] px-0.5 py-[7px]"
            >
              {({ isActive }) => (
                <>
                  <Icon d={item.d} size={17} />
                  <span className="text-center text-[9.5px] leading-[1.1] tracking-normal">
                    {item.label}
                  </span>
                  {/*
                    How many reports are waiting on the studio, on the rail
                    where somebody sees it without going to look. Only on
                    Tickets, and only when there are any: a nought in a
                    circle is a decoration that teaches people to ignore the
                    circle.
                  */}
                  {item.to === '/tickets' && waiting > 0 ? (
                    <span
                      aria-hidden="true"
                      className="absolute top-[3px] right-[7px] flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-1 text-[9px] font-semibold"
                      style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
                    >
                      {waiting > 99 ? '99+' : waiting}
                    </span>
                  ) : null}
                  {/*
                    The marker outside the button, so the active item is legible
                    at a glance down a rail of four identical squares.
                  */}
                  <span
                    aria-hidden="true"
                    className="absolute top-1/2 -left-[7px] h-4 w-0.5 -translate-y-1/2 rounded-sm"
                    style={{ background: isActive ? 'var(--accent)' : 'transparent' }}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          title="Sign out"
          aria-label="Sign out"
          onClick={() => {
            /*
             * `.finally` says the navigation happens either way; it is not what
             * makes that safe. signOut swallows its own failure — without that
             * catch this chain would still reject and the rejection would go
             * unhandled, whichever combinator is used here.
             */
            void signOut().finally(() => navigate('/login'));
          }}
          className="btn-quiet focus-ring mt-auto flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-[9px]"
          style={{ color: 'var(--dim)' }}
        >
          <Icon d="M6.2 13.2H3.4V2.8h2.8M9.6 10.6L12.4 8 9.6 5.4M6.6 8h5.6" />
        </button>
      </aside>

      <main className="flex min-w-0 flex-col">
        <header
          className="sticky top-0 z-[5] flex min-h-[50px] flex-wrap items-center gap-3 border-b px-[18px]"
          style={{
            borderColor: 'var(--line)',
            /*
              `--panel`, not `--bg`. The design moved both sticky strips onto
              the panel colour — with the light set's ground now pure white,
              a translucent header over `--bg` had nothing to separate it from
              the page scrolling underneath.
            */
            background: 'color-mix(in oklab, var(--panel) 92%, transparent)',
            backdropFilter: 'blur(14px)',
          }}
        >
          <span className="flex min-w-0 items-baseline gap-2.5">
            <h1 className="m-0 text-[15px] font-semibold tracking-[-0.015em] whitespace-nowrap">
              {title}
            </h1>
            <span className="truncate font-mono text-[10.5px]" style={{ color: 'var(--dim)' }}>
              {crumb}
            </span>
          </span>

          <span className="ml-auto flex items-center gap-2">
            {segments.length > 0 ? (
              <span
                className="inline-flex rounded-lg border p-0.5"
                style={{ background: 'var(--panel2)', borderColor: 'var(--line)' }}
              >
                {segments.map((segment) => (
                  <button
                    key={segment.label}
                    type="button"
                    onClick={segment.onClick}
                    aria-pressed={segment.pressed}
                    className="seg seg-accent focus-ring min-h-[26px] cursor-pointer px-2.5 py-[5px] text-[10.5px]"
                  >
                    {segment.label}
                  </button>
                ))}
              </span>
            ) : null}
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                className={`${
                  action.kind === 'accent' ? 'btn-accent' : 'btn-outline'
                } focus-ring inline-flex min-h-8 cursor-pointer items-center gap-1.5 px-3 py-[7px] text-[12.5px]`}
              >
                {action.icon === 'plus' ? <span aria-hidden="true">＋</span> : null}
                {action.label}
              </button>
            ))}
            {bar ? <SaveBar /> : null}

            <button
              type="button"
              onClick={toggle}
              title={themeLabel}
              aria-label={themeLabel}
              className="btn-outline focus-ring flex h-8 w-8 flex-none cursor-pointer items-center justify-center"
              style={{ background: 'var(--panel2)' }}
            >
              <Icon d={theme === 'light' ? MOON : SUN} size={15} />
            </button>

            <NavLink
              to="/settings/profile"
              title="Profile settings"
              aria-label="Open your profile settings"
              className="focus-ring flex cursor-pointer items-center gap-2 rounded-full border border-transparent py-[3px] pr-1.5 pl-[3px]"
              style={{ color: 'var(--text)' }}
            >
              {/*
                The photo, or the initial — and the two have to agree with the
                profile card, which is why both read the same `me`. Hidden from
                assistive software either way: the name is written out right
                beside it.
              */}
              {user?.avatarUrl ? (
                <img
                  aria-hidden="true"
                  src={user.avatarUrl}
                  alt=""
                  className="h-7 w-7 flex-none rounded-full object-cover"
                  style={{ border: '1px solid var(--line2)' }}
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="flex h-7 w-7 flex-none items-center justify-center rounded-full text-[11.5px] font-semibold"
                  style={{ background: 'var(--in)', border: '1px solid var(--line2)' }}
                >
                  {initial}
                </span>
              )}
              <span className="hidden flex-col leading-[1.25] sm:flex">
                <span className="text-xs font-medium whitespace-nowrap">{name}</span>
                <span
                  className="text-left font-mono text-[10px] tracking-normal"
                  style={{ color: 'var(--dim)' }}
                >
                  {user?.role ?? ''}
                </span>
              </span>
            </NavLink>
          </span>
        </header>

        <Outlet />
      </main>

      <Toast />
    </div>
  );
}
